import {
  AttributesView,
  BreakdownBar,
  useModelColorMap,
} from "@bagel-web/components";

import type { Usage } from "../types";
import {
  BarReportSectionContainer,
  BarReportSectionHeader,
  ReportLabel,
  ReportSectionContainer,
} from "./BarReport";
import { formatCurrency, formatNumber } from "../util";

const UsageTooltip = ({
  usage,
  usageKey,
  getCost,
}: {
  usage: Usage;
  usageKey?: string;
  getCost?: (usage: Usage[] | Usage) => number;
}) => {
  const attributes = [
    {
      name: "model",
      label: "Model",
      value: `${usage.service_alias_name}`,
      showMonoValue: true,
      showBoldLabel: usageKey === "model",
      showBoldValue: usageKey === "model",
    },
  ];

  if (usageKey === "cost" && getCost) {
    attributes.push({
      name: "cost",
      label: "Cost (USD)",
      value: `${formatCurrency(getCost(usage))}`,
      showMonoValue: false,
      showBoldLabel: true,
      showBoldValue: true,
    });
  } else {
    attributes.push({
      name: "requests",
      label: "Requests",
      value: `${formatNumber(usage.requests)}`,
      showMonoValue: false,
      showBoldLabel: usageKey === "requests",
      showBoldValue: usageKey === "requests",
    });

    if (usage.input_tokens) {
      attributes.push({
        name: "input_tokens",
        label: "Input Tokens",
        value: `${formatNumber(usage.input_tokens)}`,
        showMonoValue: false,
        showBoldLabel: usageKey === "input_tokens",
        showBoldValue: usageKey === "input_tokens",
      });
    }

    if (usage.output_tokens) {
      attributes.push({
        name: "output_tokens",
        label: "Output Tokens",
        value: `${formatNumber(usage.output_tokens)}`,
        showMonoValue: false,
        showBoldLabel: usageKey === "output_tokens",
        showBoldValue: usageKey === "output_tokens",
      });
    }
  }

  return <AttributesView attributes={attributes} />;
};

const mergeUsageCounts = (l: Usage, r: Usage): Usage => {
  return {
    ...l,
    requests: l.requests + r.requests,
    input_tokens: (l.input_tokens ?? 0) + (r.input_tokens ?? 0),
    output_tokens: (l.output_tokens ?? 0) + (r.output_tokens ?? 0),
  };
};

const UsageSummaryBar = ({
  usage,
  usageKey,
  getCost,
}: {
  usage?: Usage[];
  usageKey: string;
  getCost?: (usage: Usage[] | Usage) => number;
}) => {
  const { getColors } = useModelColorMap();

  // Merge usage counts by service alias name to handle historical data
  const usageMap =
    usage?.reduce(
      (acc, u) => {
        if (acc[u.service_alias_name]) {
          acc[u.service_alias_name] = mergeUsageCounts(
            acc[u.service_alias_name]!,
            u
          );
        } else {
          acc[u.service_alias_name] = { ...u };
        }
        return acc;
      },
      {} as Record<string, Usage>
    ) || {};

  return (
    usage && (
      <BreakdownBar
        segments={Object.values(usageMap).map((u) => {
          return {
            size:
              usageKey === "cost" && getCost
                ? getCost(u)
                : Number(u[usageKey as keyof Usage]),
            tooltip: (
              <UsageTooltip usage={u} usageKey={usageKey} getCost={getCost} />
            ),
            borderColor: getColors(u.service_alias_name).borderColor,
            fillColor: getColors(u.service_alias_name).backgroundColor,
          };
        })}
      />
    )
  );
};

const sortUsage = (
  usageKey: string,
  usage?: Usage[],
  getCost?: (usage: Usage[] | Usage) => number
): Usage[] | undefined => {
  return usage?.toSorted((l, r) => {
    if (usageKey === "cost" && getCost) {
      return getCost(r) - getCost(l);
    } else {
      return (
        Number(r[usageKey as keyof Usage]) - Number(l[usageKey as keyof Usage])
      );
    }
  });
};

const getTotalUsage = (
  usageKey: string,
  usage?: Usage[],
  getCost?: (usage: Usage[]) => number
): number => {
  if (usageKey === "cost" && getCost) {
    return (usage && getCost(usage)) || 0;
  } else {
    return (
      usage?.reduce((sum, u) => sum + Number(u[usageKey as keyof Usage]), 0) ||
      0
    );
  }
};

const getFieldLabel = (usageKey: string, total: number) => {
  const plural = total !== 1;
  if (usageKey === "requests") {
    return plural ? "requests" : "request";
  } else if (usageKey === "input_tokens") {
    return plural ? "input tokens" : "input token";
  } else if (usageKey === "output_tokens") {
    return plural ? "output tokens" : "output token";
  } else if (usageKey === "cost") {
    return "";
  }
};

const getTotalLabel = (
  showFieldLabel: boolean,
  usageKey: string,
  usage?: Usage[],
  getCost?: (usage: Usage[]) => number
): string => {
  const totalUsage = getTotalUsage(usageKey, usage, getCost);

  if (usageKey === "cost") {
    return formatCurrency(totalUsage);
  }
  return `${formatNumber(totalUsage)} ${
    showFieldLabel ? getFieldLabel(usageKey, totalUsage) : ""
  }`;
};

const SummarySection = ({
  usage,
  title,
  scope,
  timestamp,
  showFieldLabel = false,
  usageKey = "requests",
  getCost,
}: {
  usage?: Usage[];
  title: string;
  scope: string;
  timestamp?: string;
  showFieldLabel?: boolean;
  usageKey?: string;
  getCost?: (usage: Usage[] | Usage) => number;
}) => {
  const filteredUsage = usage?.filter(
    (u) => u.scope === scope && (!timestamp || u.timestamp === timestamp)
  );
  const sortedUsage = sortUsage(usageKey, filteredUsage, getCost);
  return (
    <BarReportSectionContainer>
      <BarReportSectionHeader>
        <h6>
          <ReportLabel>{title}</ReportLabel>
        </h6>
        <h6>
          <ReportLabel>
            {getTotalLabel(showFieldLabel, usageKey, sortedUsage, getCost)}
          </ReportLabel>
        </h6>
      </BarReportSectionHeader>
      <UsageSummaryBar
        usage={sortedUsage}
        usageKey={usageKey}
        getCost={getCost}
      />
    </BarReportSectionContainer>
  );
};

type ReportSection = {
  title: string;
  scope: string;
  timestamp?: string;
};

const UsageSummaryReport = ({
  usage,
  sections,
  showFieldLabel = false,
  summaryField = "requests",
  getCost,
}: {
  usage?: Usage[];
  sections?: ReportSection[];
  showFieldLabel?: boolean;
  summaryField?: string;
  getCost?: (usage: Usage[] | Usage) => number;
}) => {
  return (
    <ReportSectionContainer>
      {sections ? (
        sections.map(({ scope, timestamp, title }) => {
          return (
            <SummarySection
              key={`usage-${scope}-${timestamp}`}
              usage={usage}
              title={title}
              scope={scope}
              timestamp={timestamp}
              showFieldLabel={showFieldLabel}
              usageKey={summaryField}
              getCost={getCost}
            />
          );
        })
      ) : (
        <>
          <SummarySection
            usage={usage}
            title="Today"
            scope="daily"
            showFieldLabel={showFieldLabel}
            usageKey={summaryField}
            getCost={getCost}
          />
          <SummarySection
            usage={usage}
            title="Month to Date"
            scope="monthly"
            showFieldLabel={showFieldLabel}
            usageKey={summaryField}
            getCost={getCost}
          />
        </>
      )}
    </ReportSectionContainer>
  );
};

export default UsageSummaryReport;
